import type { StateCreator } from 'zustand'
import { CRITERIOS_VAZIOS, type CriteriosDosPins, type PinTopic } from '@viewer/bcf/lib/pinFilter'

/** O que a vista faz aos pins neste momento, publicado pelo overlay para
 *  o painel explicar porque é que há pins que não se vêem. */
export interface ResumoDaVistaDosPins {
  /** Pins tapados pela geometria (testados quando a câmara parou). */
  tapados: number
  /** Pins fora do volume da caixa ou dos planos de corte. */
  foraDoCorte: number
  /** Bolhas desenhadas (cada uma com dois ou mais pins). */
  bolhas: number
}

/** Aberto/fechado dos painéis que vieram com o pacote de extensões.
 *
 *  Existe para eles viverem onde vivem os outros: o Comparar IFC, o IDS
 *  e os Conflitos guardam aqui o seu estado, e é isso que dá ao botão
 *  do friso o aspecto de premido e ao `closeSession` uma maneira de os
 *  fechar. Como chegaram, cada um guardava o seu `useState` e o friso
 *  falava com eles por eventos do `window`: funcionava, mas o botão
 *  nunca sabia dizer se o painel estava aberto, e fechar a sessão
 *  deixava-os pendurados sobre uma cena vazia. */
export interface ExtraPanelsSlice {
  pdfReviewOpen: boolean
  setPdfReviewOpen: (open: boolean) => void
  /** A revisão de PDF vive numa janela do browser à parte. Serve para
   *  ter o desenho num monitor e o modelo no outro, que é como se
   *  trabalha quando se está a conferir um contra o outro. */
  pdfReviewDetached: boolean
  setPdfReviewDetached: (detached: boolean) => void
  versionHistoryOpen: boolean
  setVersionHistoryOpen: (open: boolean) => void
  pointCloudPanelOpen: boolean
  setPointCloudPanelOpen: (open: boolean) => void
  /** As lentes: ver o modelo por regras. Aqui e não num useState
   *  local pela mesma razão dos outros: o botão da régua precisa de
   *  saber se está aberto, e fechar a sessão precisa de o fechar. */
  lensPanelOpen: boolean
  setLensPanelOpen: (open: boolean) => void
  /** O Documento: relatório sobre o modelo, impresso em PDF. */
  reportPanelOpen: boolean
  setReportPanelOpen: (open: boolean) => void
  /** IFC5 (.ifcx): abrir, desenhar e empilhar camadas. */
  ifcxPanelOpen: boolean
  setIfcxPanelOpen: (open: boolean) => void
  /** Planos de corte soltos, vários ao mesmo tempo. */
  planosCortePanelOpen: boolean
  setPlanosCortePanelOpen: (open: boolean) => void
  /** Dados do IFC: inventário, importar de Excel, Psets, alterações. */
  dadosPanelOpen: boolean
  setDadosPanelOpen: (open: boolean) => void
  /** Estrutura IFC (modo avançado): qualquer entidade do ficheiro, com
   *  os atributos pelo esquema, as referências e a linha STEP. */
  estruturaPanelOpen: boolean
  setEstruturaPanelOpen: (open: boolean) => void
  /** O que o painel Estrutura deve mostrar a seguir. `n` muda a cada
   *  pedido, para o mesmo #id se poder pedir duas vezes. */
  estruturaAlvo: { modelId: string; id: number; n: number } | null
  abrirNaEstrutura: (modelId: string, id: number) => void
  /** Globo 3D (Cesium) com os modelos georreferenciados. */
  globoPanelOpen: boolean
  setGloboPanelOpen: (open: boolean) => void
  /** Juntar IFC: ferramenta à parte, trabalha sobre ficheiros. */
  juntarIfcOpen: boolean
  setJuntarIfcOpen: (open: boolean) => void
  /** Entrega assinada: termo de entrega para assinar e verificação. */
  entregaAssinadaOpen: boolean
  setEntregaAssinadaOpen: (open: boolean) => void
  /** Desenhar a selecção por cima de tudo, mesmo tapada por outros
   *  elementos. Preferência de quem trabalha: não se fecha com a sessão. */
  selecaoAtravessa: boolean
  setSelecaoAtravessa: (v: boolean) => void
  modelIntelligenceOpen: boolean
  setModelIntelligenceOpen: (open: boolean) => void
  /** Os pins BCF sobre o modelo estão visíveis. Um marcador por tópico
   *  com viewpoint enche depressa: uma detecção de conflitos triada em
   *  BCF põe um pin por conflito. Tem de haver por onde os desligar. */
  bcfPinsVisible: boolean
  setBcfPinsVisible: (visible: boolean) => void
  /** Fechar o painel também o tira da janela destacada. */
  bcfPinsPanelOpen: boolean
  setBcfPinsPanelOpen: (open: boolean) => void
  /** O painel dos pins está numa janela do browser à parte. Não se
   *  guarda: ao reabrir a aplicação não houve gesto e o browser
   *  bloquearia a janela. */
  bcfPinsPanelDestacado: boolean
  setBcfPinsPanelDestacado: (v: boolean) => void
  bcfPinsHideClosed: boolean
  setBcfPinsHideClosed: (hide: boolean) => void
  /** Os pins seguem a lista do módulo BCF (filtros e blocos abertos).
   *  Ligado por omissão: com centenas de conflitos em BCF é o que torna os
   *  pins legíveis. */
  bcfPinsSeguirModulo: boolean
  setBcfPinsSeguirModulo: (v: boolean) => void
  /** Os critérios do painel (pesquisa, prioridade, estado, grupo,
   *  etiquetas, responsável, origem). Vazios: todos. Marcado = só esses.
   *
   *  Até à beta.176 o estado e a origem eram listas de ESCONDIDOS
   *  (`bcfPinsHiddenStatuses`, `bcfPinsHiddenSources`). Não há migração
   *  porque não há nada guardado a migrar: esses filtros só viviam em
   *  memória (nem localStorage, nem sessão), e a versão nova chega com a
   *  página recarregada, isto é, com eles vazios. */
  bcfPinsCriterios: CriteriosDosPins
  setBcfPinsCriterios: (muda: Partial<CriteriosDosPins>) => void
  /** "Limpar filtros": os critérios e o esconder fechados. Não mexe no
   *  seguir o módulo nem nas opções de desenho. */
  limparFiltrosDosPins: () => void
  /** Esconder os pins tapados pela geometria, em vez de os esbater.
   *  Desligado por omissão: um pin esbatido ainda diz que ali há alguma
   *  coisa. */
  bcfPinsEsconderTapados: boolean
  setBcfPinsEsconderTapados: (v: boolean) => void
  /** Com corte activo, esconder os pins fora do volume visível (por
   *  omissão); desligado, esbatem-se. */
  bcfPinsEsconderForaDoCorte: boolean
  setBcfPinsEsconderForaDoCorte: (v: boolean) => void
  bcfPinsResumoVista: ResumoDaVistaDosPins
  setBcfPinsResumoVista: (r: ResumoDaVistaDosPins) => void
  /** Os tópicos que hoje têm pin, publicados pelo overlay para o painel
   *  os poder listar e contar sem repetir a leitura do IndexedDB. */
  bcfPinTopics: PinTopic[]
  setBcfPinTopics: (topics: PinTopic[]) => void
  closeExtraPanels: () => void
}

export const createExtraPanelsSlice: StateCreator<ExtraPanelsSlice> = (set) => ({
  pdfReviewOpen: false,
  setPdfReviewOpen: (open) => set({ pdfReviewOpen: open }),
  pdfReviewDetached: false,
  setPdfReviewDetached: (detached) => set({ pdfReviewDetached: detached }),
  versionHistoryOpen: false,
  setVersionHistoryOpen: (open) => set({ versionHistoryOpen: open }),
  pointCloudPanelOpen: false,
  setPointCloudPanelOpen: (open) => set({ pointCloudPanelOpen: open }),
  modelIntelligenceOpen: false,
  setModelIntelligenceOpen: (open) => set({ modelIntelligenceOpen: open }),
  bcfPinsVisible: true,
  setBcfPinsVisible: (visible) => set({ bcfPinsVisible: visible }),
  bcfPinsPanelOpen: false,
  setBcfPinsPanelOpen: (open) =>
    set(open ? { bcfPinsPanelOpen: true } : { bcfPinsPanelOpen: false, bcfPinsPanelDestacado: false }),
  bcfPinsPanelDestacado: false,
  setBcfPinsPanelDestacado: (v) => set({ bcfPinsPanelDestacado: v }),
  lensPanelOpen: false,
  setLensPanelOpen: (open) => set({ lensPanelOpen: open }),
  reportPanelOpen: false,
  setReportPanelOpen: (open) => set({ reportPanelOpen: open }),
  ifcxPanelOpen: false,
  setIfcxPanelOpen: (open) => set({ ifcxPanelOpen: open }),
  planosCortePanelOpen: false,
  setPlanosCortePanelOpen: (open) => set({ planosCortePanelOpen: open }),
  dadosPanelOpen: false,
  setDadosPanelOpen: (open) => set({ dadosPanelOpen: open }),
  estruturaPanelOpen: false,
  setEstruturaPanelOpen: (open) => set({ estruturaPanelOpen: open }),
  estruturaAlvo: null,
  abrirNaEstrutura: (modelId, id) =>
    set((s) => ({ estruturaPanelOpen: true, estruturaAlvo: { modelId, id, n: (s.estruturaAlvo?.n ?? 0) + 1 } })),
  globoPanelOpen: false,
  setGloboPanelOpen: (open) => set({ globoPanelOpen: open }),
  juntarIfcOpen: false,
  setJuntarIfcOpen: (open) => set({ juntarIfcOpen: open }),
  entregaAssinadaOpen: false,
  setEntregaAssinadaOpen: (open) => set({ entregaAssinadaOpen: open }),
  selecaoAtravessa: true,
  setSelecaoAtravessa: (v) => set({ selecaoAtravessa: v }),
  bcfPinsHideClosed: false,
  setBcfPinsHideClosed: (hide) => set({ bcfPinsHideClosed: hide }),
  bcfPinsSeguirModulo: true,
  setBcfPinsSeguirModulo: (v) => set({ bcfPinsSeguirModulo: v }),
  bcfPinsCriterios: CRITERIOS_VAZIOS,
  setBcfPinsCriterios: (muda) => set((s) => ({ bcfPinsCriterios: { ...s.bcfPinsCriterios, ...muda } })),
  limparFiltrosDosPins: () =>
    set({
      bcfPinsCriterios: CRITERIOS_VAZIOS,
      bcfPinsHideClosed: false,
    }),
  bcfPinsEsconderTapados: false,
  setBcfPinsEsconderTapados: (v) => set({ bcfPinsEsconderTapados: v }),
  bcfPinsEsconderForaDoCorte: true,
  setBcfPinsEsconderForaDoCorte: (v) => set({ bcfPinsEsconderForaDoCorte: v }),
  bcfPinsResumoVista: { tapados: 0, foraDoCorte: 0, bolhas: 0 },
  setBcfPinsResumoVista: (r) =>
    set((s) => {
      const a = s.bcfPinsResumoVista
      // Publicado a cada fotograma da câmara: só muda o estado quando os
      // números mudam, para o painel não redesenhar à toa.
      if (a.tapados === r.tapados && a.foraDoCorte === r.foraDoCorte && a.bolhas === r.bolhas) return {}
      return { bcfPinsResumoVista: r }
    }),
  bcfPinTopics: [],
  setBcfPinTopics: (topics) => set({ bcfPinTopics: topics }),
  closeExtraPanels: () =>
    set({
      pdfReviewOpen: false,
      pdfReviewDetached: false,
      versionHistoryOpen: false,
      pointCloudPanelOpen: false,
      modelIntelligenceOpen: false,
      bcfPinsPanelOpen: false,
      bcfPinsPanelDestacado: false,
      lensPanelOpen: false,
      reportPanelOpen: false,
      ifcxPanelOpen: false,
      planosCortePanelOpen: false,
      dadosPanelOpen: false,
      estruturaPanelOpen: false,
      estruturaAlvo: null,
      globoPanelOpen: false,
      juntarIfcOpen: false,
      entregaAssinadaOpen: false,
      // A lista de tópicos vem dos BCF carregados, que a sessão leva
      // consigo. Sem a limpar, o painel continuava a contar pins de
      // ficheiros que já ninguém tem abertos.
      bcfPinTopics: [],
      // A visibilidade e os filtros dos pins são preferência de quem
      // trabalha, não estado da sessão: sobrevivem a fechar a sessão,
      // como o snap das vistas 2D.
    }),
})
